/**
 * Login window — Sign In / Request Account tabs, plus deep-link flows for
 * email verification (`?verify=…`) and password reset (`?reset=…`).
 *
 * Rendered in an XP window via the appSettings registry. On success, closes
 * itself (via the onClose callback provided by the window manager).
 *
 * Props (optional, set by App.js when handling ?verify / ?reset URLs):
 *   initialMode: 'signIn' | 'request' | 'forgot' | 'reset' | 'verify'
 *   verifySelector / verifyToken: for initialMode='verify'
 *   resetSelector  / resetToken:  for initialMode='reset'
 */

import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { useAuth } from 'auth/AuthContext';
import { ApiError } from 'api/client';

import userIcon from 'assets/windowsIcons/user.png';
import keyIcon from 'assets/windowsIcons/546(32x32).png';

const TAB_SIGN_IN = 'signIn';
const TAB_REQUEST = 'request';
const MODE_REQUEST_SENT = 'requestSent';
const MODE_FORGOT = 'forgot';
const MODE_FORGOT_SENT = 'forgotSent';
const MODE_RESET = 'reset';
const MODE_RESET_DONE = 'resetDone';
const MODE_VERIFY = 'verify';
const MODE_VERIFY_DONE = 'verifyDone';
const MODE_VERIFY_FAILED = 'verifyFailed';

function Login({
  onClose,
  initialMode,
  verifySelector,
  verifyToken,
  resetSelector,
  resetToken,
}) {
  const {
    login,
    requestSignup,
    forgotPassword,
    resetPassword,
    verifyEmail,
  } = useAuth();
  const [tab, setTab] = useState(
    initialMode === TAB_REQUEST || initialMode === 'redeem'
      ? TAB_REQUEST
      : TAB_SIGN_IN,
  );
  // `mode` overlays `tab` — when set, it takes over the body region.
  const [mode, setMode] = useState(
    initialMode === MODE_FORGOT ||
      initialMode === MODE_RESET ||
      initialMode === MODE_VERIFY
      ? initialMode
      : null,
  );

  // Sign-in tab state.
  const [signInUsername, setSignInUsername] = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  const [remember, setRemember] = useState(false);

  // Request-account tab state.
  const [reqUsername, setReqUsername] = useState('');
  const [reqDisplayName, setReqDisplayName] = useState('');
  const [reqEmail, setReqEmail] = useState('');
  const [reqReason, setReqReason] = useState('');

  // Forgot / reset / verify state.
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Kick off the verify request as soon as the window opens in verify mode.
  const verifyAttemptedRef = useRef(false);
  useEffect(() => {
    if (
      mode === MODE_VERIFY &&
      verifySelector &&
      verifyToken &&
      !verifyAttemptedRef.current
    ) {
      verifyAttemptedRef.current = true;
      setBusy(true);
      setError(null);
      verifyEmail(verifySelector, verifyToken)
        .then(() => {
          setMode(MODE_VERIFY_DONE);
        })
        .catch(err => {
          setError(errorMessage(err));
          setMode(MODE_VERIFY_FAILED);
        })
        .finally(() => setBusy(false));
    }
  }, [mode, verifySelector, verifyToken, verifyEmail]);

  async function submitSignIn(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await login(signInUsername.trim(), signInPassword, remember);
      onClose && onClose();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  async function submitRequest(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await requestSignup({
        username: reqUsername.trim(),
        display_name: reqDisplayName.trim() || reqUsername.trim(),
        email: reqEmail.trim(),
        reason: reqReason.trim(),
      });
      setMode(MODE_REQUEST_SENT);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitForgot(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await forgotPassword(forgotEmail.trim());
      setMode(MODE_FORGOT_SENT);
    } catch (err) {
      // 400 invalid_email is the only failure that surfaces; all others
      // return ok:true from the server so we don't leak account existence.
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitReset(e) {
    e.preventDefault();
    if (busy || !resetSelector || !resetToken) return;
    setBusy(true);
    setError(null);
    try {
      await resetPassword(resetSelector, resetToken, resetNewPassword);
      setMode(MODE_RESET_DONE);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Div>
      <header className="login__header">
        <img src={userIcon} alt="" className="login__header__icon" />
        <div className="login__header__text">
          <div className="login__header__title">Log On to lottel.fun</div>
          <div className="login__header__subtitle">
            Members only. Apply below — lottel approves signups by hand.
          </div>
        </div>
      </header>

      <div className="login__tabs">
        <button
          type="button"
          className={
            'login__tab' +
            (!mode && tab === TAB_SIGN_IN ? ' login__tab--active' : '')
          }
          onClick={() => {
            setMode(null);
            setTab(TAB_SIGN_IN);
            setError(null);
          }}
          disabled={busy}
        >
          Sign In
        </button>
        <button
          type="button"
          className={
            'login__tab' +
            (!mode && tab === TAB_REQUEST ? ' login__tab--active' : '')
          }
          onClick={() => {
            setMode(null);
            setTab(TAB_REQUEST);
            setError(null);
          }}
          disabled={busy}
        >
          Request Account
        </button>
      </div>

      <section className="login__body">
        {mode === MODE_FORGOT ? (
          <form onSubmit={submitForgot}>
            <p className="login__blurb">
              Enter the email address on your account and we&apos;ll send you a
              link to reset your password.
            </p>
            <Field label="Email">
              <input
                type="email"
                autoComplete="email"
                autoFocus
                value={forgotEmail}
                onChange={e => setForgotEmail(e.target.value)}
                disabled={busy}
                required
              />
            </Field>
            {error && <div className="login__error">{error}</div>}
            <div className="login__actions">
              <button type="submit" className="login__button" disabled={busy}>
                {busy ? 'Sending…' : 'Send reset link'}
              </button>
              <button
                type="button"
                className="login__button"
                onClick={() => {
                  setMode(null);
                  setError(null);
                }}
                disabled={busy}
              >
                Back
              </button>
            </div>
          </form>
        ) : mode === MODE_FORGOT_SENT ? (
          <div className="login__done">
            <p>
              If that email is on file, we just sent a password-reset link to
              it. Check your inbox (and spam folder).
            </p>
            <div className="login__actions">
              <button
                type="button"
                className="login__button"
                onClick={() => {
                  setMode(null);
                  setError(null);
                }}
              >
                Back to sign in
              </button>
            </div>
          </div>
        ) : mode === MODE_RESET ? (
          <form onSubmit={submitReset}>
            <p className="login__blurb">
              Choose a new password for your account.
            </p>
            <Field label="New password">
              <input
                type="password"
                autoComplete="new-password"
                autoFocus
                value={resetNewPassword}
                onChange={e => setResetNewPassword(e.target.value)}
                disabled={busy}
                required
                minLength={8}
              />
            </Field>
            {error && <div className="login__error">{error}</div>}
            <div className="login__actions">
              <button type="submit" className="login__button" disabled={busy}>
                {busy ? 'Saving…' : 'Set new password'}
              </button>
              <button
                type="button"
                className="login__button"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : mode === MODE_RESET_DONE ? (
          <div className="login__done">
            <p>Password updated. You are now signed in.</p>
            <div className="login__actions">
              <button type="button" className="login__button" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        ) : mode === MODE_VERIFY ? (
          <div className="login__done">
            <p>{busy ? 'Verifying your email…' : 'One moment…'}</p>
          </div>
        ) : mode === MODE_VERIFY_DONE ? (
          <div className="login__done">
            <p>Thanks — your email is verified.</p>
            <div className="login__actions">
              <button type="button" className="login__button" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        ) : mode === MODE_VERIFY_FAILED ? (
          <div className="login__done">
            <p>{error || 'That verification link is not valid.'}</p>
            <div className="login__actions">
              <button
                type="button"
                className="login__button"
                onClick={() => {
                  setMode(null);
                  setError(null);
                }}
              >
                Back to sign in
              </button>
            </div>
          </div>
        ) : mode === MODE_REQUEST_SENT ? (
          <div className="login__done">
            <p>
              Thanks — your request is in. Lottel will review it and, if
              approved, you&apos;ll get an email with a link to set your
              password.
            </p>
            <div className="login__actions">
              <button
                type="button"
                className="login__button"
                onClick={() => {
                  setMode(null);
                  setTab(TAB_SIGN_IN);
                  setError(null);
                  setReqUsername('');
                  setReqDisplayName('');
                  setReqEmail('');
                  setReqReason('');
                }}
              >
                Back to sign in
              </button>
              <button type="button" className="login__button" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        ) : tab === TAB_SIGN_IN ? (
          <form onSubmit={submitSignIn}>
            <Field label="User name">
              <input
                type="text"
                autoComplete="username"
                autoFocus
                value={signInUsername}
                onChange={e => setSignInUsername(e.target.value)}
                disabled={busy}
                required
              />
            </Field>
            <Field label="Password">
              <input
                type="password"
                autoComplete="current-password"
                value={signInPassword}
                onChange={e => setSignInPassword(e.target.value)}
                disabled={busy}
                required
              />
            </Field>
            <label className="login__remember">
              <input
                type="checkbox"
                checked={remember}
                onChange={e => setRemember(e.target.checked)}
                disabled={busy}
              />
              Remember me on this computer
            </label>
            {error && <div className="login__error">{error}</div>}
            <div className="login__actions">
              <button type="submit" className="login__button" disabled={busy}>
                {busy ? 'Signing in…' : 'Sign In'}
              </button>
              <button
                type="button"
                className="login__button"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </button>
            </div>
            <div className="login__forgot">
              <button
                type="button"
                className="login__link"
                onClick={() => {
                  setMode(MODE_FORGOT);
                  setForgotEmail('');
                  setError(null);
                }}
                disabled={busy}
              >
                Forgot password?
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={submitRequest}>
            <div className="login__redeem-icon">
              <img src={keyIcon} alt="" />
              <p>
                Applications are reviewed by hand. Once approved, you&apos;ll
                get an email with a link to set your password.
              </p>
            </div>
            <Field label="User name">
              <input
                type="text"
                autoComplete="username"
                autoFocus
                value={reqUsername}
                onChange={e => setReqUsername(e.target.value)}
                disabled={busy}
                required
                minLength={2}
                maxLength={32}
                pattern="[A-Za-z0-9_.\-]+"
                title="Letters, digits, . _ -"
              />
            </Field>
            <Field label="Display name">
              <input
                type="text"
                value={reqDisplayName}
                onChange={e => setReqDisplayName(e.target.value)}
                disabled={busy}
                maxLength={64}
                placeholder="(optional — defaults to user name)"
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                autoComplete="email"
                value={reqEmail}
                onChange={e => setReqEmail(e.target.value)}
                disabled={busy}
                required
              />
            </Field>
            <Field label="Why do you want an account?">
              <textarea
                value={reqReason}
                onChange={e => setReqReason(e.target.value)}
                disabled={busy}
                maxLength={500}
                rows={3}
                placeholder="(optional — helps lottel decide)"
              />
            </Field>
            {error && <div className="login__error">{error}</div>}
            <div className="login__actions">
              <button type="submit" className="login__button" disabled={busy}>
                {busy ? 'Sending…' : 'Send Request'}
              </button>
              <button
                type="button"
                className="login__button"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </section>
    </Div>
  );
}

function Field({ label, children }) {
  return (
    <label className="login__field">
      <span className="login__field__label">{label}</span>
      {children}
    </label>
  );
}

function errorMessage(err) {
  if (err instanceof ApiError) {
    const map = {
      invalid_credentials: 'Incorrect user name or password.',
      csrf_invalid: 'Session expired — please reload the page.',
      rate_limited: 'Too many attempts. Please wait and try again.',
      email_taken: 'That email is already registered. Try signing in.',
      username_taken: 'That user name is taken. Please choose another.',
      invalid_username: 'User names must be 2-32 letters/digits/._-',
      invalid_password: 'Passwords must be at least 8 characters.',
      invalid_email: 'That email address does not look valid.',
      invalid_token: 'That link is not valid. It may have already been used.',
      token_expired: 'That link has expired. Please request a new one.',
      network_error: 'Cannot reach the server. Check your connection.',
    };
    return map[err.code] || err.message || 'Something went wrong.';
  }
  return (err && err.message) || 'Something went wrong.';
}

const Div = styled.div`
  background: #ece9d8;
  color: #000;
  font-family: Tahoma, sans-serif;
  font-size: 11px;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 10px 12px 12px;
  box-sizing: border-box;

  .login__header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-bottom: 8px;
    border-bottom: 1px solid #a4b0be;
  }
  .login__header__icon {
    width: 40px;
    height: 40px;
    image-rendering: pixelated;
  }
  .login__header__title {
    font-weight: bold;
    font-size: 12px;
    color: #0b3d91;
  }
  .login__header__subtitle {
    color: #444;
    margin-top: 2px;
  }

  .login__tabs {
    display: flex;
    margin-top: 8px;
    gap: 2px;
  }
  .login__tab {
    background: #d6d3c4;
    border: 1px solid #7d7963;
    border-bottom: none;
    padding: 4px 12px;
    font: inherit;
    cursor: pointer;
    border-top-left-radius: 3px;
    border-top-right-radius: 3px;
  }
  .login__tab--active {
    background: #ece9d8;
    font-weight: bold;
    position: relative;
    z-index: 1;
  }

  .login__body {
    border: 1px solid #7d7963;
    background: #ece9d8;
    padding: 14px 12px 10px;
    flex: 1;
    overflow-y: auto;
  }

  .login__field {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    gap: 8px;
  }
  .login__field__label {
    width: 96px;
    text-align: right;
  }
  .login__field input,
  .login__field textarea {
    flex: 1;
    padding: 3px 5px;
    border: 1px solid #7f9db9;
    font: inherit;
    background: #fff;
    min-width: 0;
    resize: vertical;
  }
  .login__field input:focus,
  .login__field textarea:focus {
    outline: 1px solid #316ac5;
    outline-offset: -1px;
  }

  .login__remember {
    display: flex;
    align-items: center;
    gap: 6px;
    padding-left: 104px;
    margin-bottom: 8px;
  }

  .login__redeem-icon {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
  }
  .login__redeem-icon img {
    width: 32px;
    height: 32px;
  }
  .login__redeem-icon p {
    margin: 0;
    color: #444;
  }

  .login__blurb {
    margin: 0 0 10px;
    color: #333;
    line-height: 1.4;
  }
  .login__done {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 12px;
    text-align: center;
    color: #333;
    line-height: 1.4;
  }
  .login__forgot {
    text-align: right;
    padding-top: 4px;
  }
  .login__link {
    background: none;
    border: none;
    color: #0032c9;
    text-decoration: underline;
    padding: 0;
    font: inherit;
    cursor: pointer;
  }
  .login__link[disabled] {
    color: #888;
    cursor: default;
  }

  .login__error {
    background: #ffe1e1;
    color: #8b0000;
    padding: 5px 8px;
    margin: 4px 0 8px;
    border: 1px solid #d99999;
  }

  .login__actions {
    display: flex;
    justify-content: flex-end;
    gap: 6px;
    margin-top: 6px;
  }
  .login__button {
    background: linear-gradient(to bottom, #f5f5f5, #d4d0c8);
    border: 1px solid #003c74;
    padding: 4px 14px;
    font: inherit;
    min-width: 76px;
    cursor: pointer;
    border-radius: 3px;
  }
  .login__button:hover:not(:disabled) {
    background: linear-gradient(to bottom, #ffffff, #e5e0d3);
  }
  .login__button:active:not(:disabled) {
    background: linear-gradient(to bottom, #d4d0c8, #f5f5f5);
  }
  .login__button:disabled {
    color: #7f7f7f;
    cursor: default;
  }
`;

export default Login;
